import { Descriptions, Grid, Tabs } from 'antd';
import { noop } from 'lodash/fp';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import * as permissionKind from '../../../../../shared/permissions';
import { type BankModuleWithPermissionsSpecsFragment } from '../../../../api/fragments/BankModuleWithPermissionsSpecs';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions, { type PermissionValues } from '../../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../../utilities/useFormattedSimpleVersioning';
import BankModuleActions from './BankModuleActions';
import BankModuleTable from './BankModuleTable';
import {
    type OnRemoteFPSettingSubmitted,
    type OpenRemoteFPSettingDrawer,
    type SetOnRemoteFPSettingSubmitted,
    useRemoteFPSettingDrawer,
} from './RemoteFPSettingDrawer';
import RemoteFPSettingList from './RemoteFPSettingList';

export enum BankModuleTab {
    BankList = 'BankList',
    RemoteFpSettingList = 'RemoteFpSettingList',
}
type BankModulePageInnerProps = {
    module: BankModuleWithPermissionsSpecsFragment;
    currentTab: BankModuleTab;
    openRemoteFPSettingDrawer: OpenRemoteFPSettingDrawer;
    setOnRemoteFPSettingSubmitted: SetOnRemoteFPSettingSubmitted;
    permissions: Pick<PermissionValues, 'hasCreatePermission' | 'hasUpdatePermission' | 'hasDeletePermission'>;
};
const BankModulePageInner = ({
    module,
    currentTab,
    openRemoteFPSettingDrawer,
    setOnRemoteFPSettingSubmitted,
    permissions,
}: BankModulePageInnerProps) => {
    switch (currentTab) {
        case BankModuleTab.BankList:
            return <BankModuleTable module={module} permissions={permissions} />;

        case BankModuleTab.RemoteFpSettingList:
            return (
                <RemoteFPSettingList
                    module={module}
                    openDrawer={openRemoteFPSettingDrawer}
                    permissions={permissions}
                    setOnRemoteFPSettingSubmitted={setOnRemoteFPSettingSubmitted}
                />
            );

        default:
            return null;
    }
};

export type BankModulePageProps = { module: BankModuleWithPermissionsSpecsFragment };

const BankModulePage = ({ module }: BankModulePageProps) => {
    const { t } = useTranslation(['bankModuleDetails']);
    const navigate = useNavigate();
    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: module.versioning,
        timeZone: module.company.timeZone,
    });
    const screens = Grid.useBreakpoint();

    const [currentTab, setCurrentTab] = useState(BankModuleTab.BankList);
    const onTabChange = useCallback((activeKey: BankModuleTab) => {
        setCurrentTab(activeKey);
    }, []);

    const footer = (
        <Tabs activeKey={currentTab} onChange={onTabChange}>
            <Tabs.TabPane key={BankModuleTab.BankList} tab={t('bankModuleDetails:tabs.bankList')} />
            <Tabs.TabPane
                key={BankModuleTab.RemoteFpSettingList}
                tab={t('bankModuleDetails:tabs.remoteFpSettingList')}
            />
        </Tabs>
    );
    const [onRemoteFPSettingSubmitted, setOnRemoteFPSettingSubmitted] = useState<OnRemoteFPSettingSubmitted>(noop);
    const remoteFPSettingDrawer = useRemoteFPSettingDrawer(module.id, onRemoteFPSettingSubmitted);

    const permissions = {
        hasCreatePermission: hasPermissions(module.permissions, [permissionKind.createBank]),
        hasUpdatePermission: hasPermissions(module.permissions, [permissionKind.updateModule]),
        hasDeletePermission: hasPermissions(module.permissions, [permissionKind.deleteModule]),
    };

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('bankModuleDetails:content.displayName')}>
                {module.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('bankModuleDetails:content.company')}>
                {module.company.displayName}
            </Descriptions.Item>
            <Descriptions.Item {...t('common:fields.updated', { returnObjects: true, offset })}>
                {updated}
            </Descriptions.Item>
        </Descriptions>
    );

    return (
        <ConsolePageWithHeader
            content={content}
            extra={
                <BankModuleActions
                    module={module}
                    openRemoteFPSettingDrawer={remoteFPSettingDrawer.open}
                    permissions={permissions}
                />
            }
            header={{ footer }}
            onBack={() => navigate('/admin/system/modules')}
            title={screens.md ? t('bankModuleDetails:title', { name: module.displayName }) : module.displayName}
        >
            <BankModulePageInner
                currentTab={currentTab}
                module={module}
                openRemoteFPSettingDrawer={remoteFPSettingDrawer.open}
                permissions={permissions}
                setOnRemoteFPSettingSubmitted={setOnRemoteFPSettingSubmitted}
            />
            {remoteFPSettingDrawer.render()}
        </ConsolePageWithHeader>
    );
};

export default BankModulePage;
